"use client";

import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";

import Button from "@/components/atoms/Button";
import Icon from "@/components/atoms/Icon";
import { useTranslation } from "@/i18n/client";
import { AllowedLanguages } from "@/types/generic";

interface EmailCorrectnessConfirmationModalProps {
  email: string;
  locale: AllowedLanguages;
  onCancel: () => void;
  onConfirm: () => void;
  open: boolean;
}

export const EmailCorrectnessConfirmationModal = ({
  email,
  open,
  onCancel,
  onConfirm,
  locale,
}: EmailCorrectnessConfirmationModalProps) => {
  const { t } = useTranslation(locale);

  const handleConfirm = () => {
    onConfirm();
  };

  const handleClose = () => {
    onCancel();
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative rounded-xl bg-white p-10 shadow-xl sm:w-full sm:max-w-xl">
                <Dialog.Title as="h4">
                  {t("components.email_correctness_confirmation_modal.title")}
                </Dialog.Title>
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="ml-auto text-gray-700"
                    onClick={handleCancel}
                  >
                    <span className="sr-only">Close</span>
                    <Icon name="close" className="h-5 w-5 text-black-300" />
                  </button>
                </div>

                <p
                  className="text-black-300"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      "components.email_correctness_confirmation_modal.message",
                      {
                        email: email,
                      },
                    ),
                  }}
                />

                <div className="mt-8 flex items-center justify-end gap-4">
                  <Button
                    as="button"
                    type="button"
                    arrow={false}
                    onClick={handleCancel}
                  >
                    {t(
                      "components.email_correctness_confirmation_modal.cancel",
                    )}
                  </Button>
                  <button
                    type="button"
                    className={"link"}
                    onClick={handleConfirm}
                  >
                    {t(
                      "components.email_correctness_confirmation_modal.confirm",
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EmailCorrectnessConfirmationModal;
